import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import RevenueService from '../../services/revenueService';
import ExpenseService from '../../services/expenseService';
import accountingDataFetchService from '../../services/accountingDataFetchService';
import StaffSalaryService from '../../services/staffSalaryService';
import { getCurrencySymbolFromSchool } from '../../utils/currencyFormatter';

const IncomeStatementTab = () => {
  // Redux state
  const schooldetails = useSelector(state => state.schooldetails || []);
  const currentSchool = schooldetails.length > 0 ? schooldetails[0] : null;
  
  // Get currency symbol from school details
  const currencySymbol = getCurrencySymbolFromSchool(schooldetails);
  
  // Local state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [incomeStatement, setIncomeStatement] = useState(null);
  
  // Data state
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [sales, setSales] = useState([]);
  const [teachers, setTeachers] = useState([]);
  
  // Refs to prevent infinite loops
  const isCalculatingRef = useRef(false);
  const prevDataRef = useRef({ key: null });

  // Calculate date range based on period
  const dateRange = React.useMemo(() => {
    const today = new Date();
    let start = new Date();
    let end = new Date(today);
    end.setHours(23, 59, 59, 999);

    switch (selectedPeriod) {
      case 'current':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'quarter':
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        start = quarterStart;
        start.setHours(0, 0, 0, 0);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case 'custom':
        start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
    }

    return { start, end };
  }, [selectedPeriod, startDate, endDate]);

  // Fetch data from database
  const fetchData = async () => {
    if (!currentSchool?.schoolID) {
      setError('No school selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await accountingDataFetchService.fetchAllAccountingData(currentSchool.schoolID);
      setStudents(data.students || []);
      setClasses(data.classes || []);
      setExpenses(data.expenses || []);
      setSales(data.sales || []);
      setTeachers(data.teachers || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Calculate income statement
  useEffect(() => {
    if (isCalculatingRef.current) return;
    if (!currentSchool?.schoolID) return;
    if (students.length === 0 && expenses.length === 0 && sales.length === 0) return;

    const calculateIncomeStatement = () => {
      if (isCalculatingRef.current) return;
      isCalculatingRef.current = true;

      try {
        const { start, end } = dateRange;

        // Get mainterm (current term) - matching school fees page logic
        const mainterm = schooldetails && schooldetails.length > 0 && 
                         schooldetails[0].terms && 
                         Array.isArray(schooldetails[0].terms) && 
                         schooldetails[0].terms.length > 0
          ? schooldetails[0].terms[schooldetails[0].terms.length - 1]
          : "none";

        // Calculate revenue
        const revenue = RevenueService.calculateTotalRevenue(
          students,
          sales,
          classes,
          start,
          end,
          mainterm
        );

        // Calculate detailed staff salaries from payment records
        const staffSalaries = StaffSalaryService.calculateStaffSalaries(teachers, start, end);

        // Calculate expenses (without salaries - we'll add them separately)
        const expenseBreakdown = ExpenseService.getExpenseBreakdown(
          expenses,
          [], // Pass empty array for salaries since we calculate them separately
          start,
          end
        );

        // Add staff salaries to expenses
        const totalExpenses = expenseBreakdown.total + staffSalaries.total;
        
        // Update the byCategory to include staff salaries
        if (!expenseBreakdown.byCategory['Salaries & Wages']) {
          expenseBreakdown.byCategory['Salaries & Wages'] = 0;
        }
        expenseBreakdown.byCategory['Salaries & Wages'] = staffSalaries.total;

        // Calculate net income
        const netIncome = revenue.total - totalExpenses;
        const profitMargin = revenue.total > 0
          ? ((netIncome / revenue.total) * 100)
          : 0;

        // Organize revenue breakdown
        const revenueBreakdown = {
          schoolFees: revenue.schoolFees || 0,
          sales: revenue.sales || 0,
          canteen: revenue.canteen || 0,
          otherIncome: revenue.otherIncome || 0,
          total: revenue.total || 0
        };

        // Organize expense breakdown - use staff salaries total
        const expenseBreakdownFormatted = {
          salaries: staffSalaries.total || 0, // Use calculated staff salaries
          utilities: expenseBreakdown.byCategory['Utilities'] || 0,
          maintenance: expenseBreakdown.byCategory['Maintenance & Repairs'] || 0,
          supplies: expenseBreakdown.byCategory['Supplies'] || 0,
          administrative: expenseBreakdown.byCategory['Administrative'] || 0,
          rent: expenseBreakdown.byCategory['Rent & Leasing'] || 0,
          transportation: expenseBreakdown.byCategory['Transportation'] || 0,
          insurance: expenseBreakdown.byCategory['Insurance'] || 0,
          marketing: expenseBreakdown.byCategory['Marketing'] || 0,
          other: expenseBreakdown.byCategory['Other'] || 0,
          total: totalExpenses // Include staff salaries in total
        };

        setIncomeStatement({
          revenue: revenueBreakdown,
          expenses: expenseBreakdownFormatted,
          staffSalaries: staffSalaries, // Add detailed salary data
          netIncome,
          profitMargin,
          period: {
            start: dateRange.start.toISOString().split('T')[0],
            end: dateRange.end.toISOString().split('T')[0]
          }
        });
      } catch (err) {
        console.error('Error calculating income statement:', err);
        setError('Failed to calculate income statement');
      } finally {
        isCalculatingRef.current = false;
      }
    };

    calculateIncomeStatement();
  }, [students, classes, expenses, sales, teachers, dateRange, currentSchool?.schoolID]);

  // Load data on mount
  useEffect(() => {
    if (currentSchool?.schoolID) {
      fetchData();
    }
  }, [currentSchool?.schoolID]);

  // Handle period change
  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00';
    }
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  if (loading && !incomeStatement) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error && !incomeStatement) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchData}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!incomeStatement) {
    return (
      <Box>
        <Alert severity="info">No data available. Please select a period and refresh.</Alert>
        <Button variant="contained" onClick={fetchData} sx={{ mt: 2 }}>
          Load Data
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={selectedPeriod}
            onChange={handlePeriodChange}
            label="Period"
          >
            <MenuItem value="current">Current Month</MenuItem>
            <MenuItem value="quarter">Current Quarter</MenuItem>
            <MenuItem value="year">Current Year</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </Select>
        </FormControl>
        
        {selectedPeriod === 'custom' && (
          <>
            <TextField
              label="Start Date"
              type="date"
              value={startDate.toISOString().split('T')[0]}
              onChange={(e) => setStartDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate.toISOString().split('T')[0]}
              onChange={(e) => setEndDate(new Date(e.target.value))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </>
        )}
        
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading}
        >
          Refresh Data
        </Button>
        
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Revenue
              </Typography>
              <Typography variant="h4" color="primary">
                {currencySymbol}{formatCurrency(incomeStatement.revenue.total)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Staff Salaries Card */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Staff Salaries
              </Typography>
              <Typography variant="h4" color="warning.main">
                {currencySymbol}{formatCurrency(incomeStatement.staffSalaries?.total || 0)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {incomeStatement.staffSalaries?.staffCount || 0} staff members
              </Typography>
              {incomeStatement.staffSalaries?.totalBonuses > 0 && (
                <Typography variant="caption" color="success.main" display="block">
                  Bonuses: {currencySymbol}{formatCurrency(incomeStatement.staffSalaries.totalBonuses)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Expenses
              </Typography>
              <Typography variant="h4" color="error">
                {currencySymbol}{formatCurrency(incomeStatement.expenses.total)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Net Income
              </Typography>
              <Typography 
                variant="h4" 
                color={incomeStatement.netIncome >= 0 ? 'success.main' : 'error.main'}
              >
                {currencySymbol}{formatCurrency(incomeStatement.netIncome)}
              </Typography>
              {incomeStatement.netIncome < 0 && (
                <Chip 
                  label="Loss" 
                  color="error" 
                  size="small" 
                  sx={{ mt: 1 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Staff Salaries Breakdown Section */}
      {incomeStatement.staffSalaries && incomeStatement.staffSalaries.total > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'warning.light' }}>
            <Typography variant="h6" gutterBottom>
              Staff Salaries Breakdown
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total: {currencySymbol}{formatCurrency(incomeStatement.staffSalaries.total)} | 
              Base: {currencySymbol}{formatCurrency(incomeStatement.staffSalaries.totalBase)} | 
              Bonuses: {currencySymbol}{formatCurrency(incomeStatement.staffSalaries.totalBonuses)}
            </Typography>
          </Box>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Position</strong></TableCell>
                  <TableCell align="right"><strong>Staff Count</strong></TableCell>
                  <TableCell align="right"><strong>Base Salary</strong></TableCell>
                  <TableCell align="right"><strong>Bonuses</strong></TableCell>
                  <TableCell align="right"><strong>Total</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(incomeStatement.staffSalaries.byPosition)
                  .sort((a, b) => b[1].total - a[1].total)
                  .map(([position, data]) => (
                    <TableRow key={position}>
                      <TableCell>{position}</TableCell>
                      <TableCell align="right">{data.count}</TableCell>
                      <TableCell align="right">{formatCurrency(data.base)}</TableCell>
                      <TableCell align="right">{formatCurrency(data.bonuses)}</TableCell>
                      <TableCell align="right">
                        <strong>{currencySymbol}{formatCurrency(data.total)}</strong>
                      </TableCell>
                    </TableRow>
                  ))}
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>TOTAL</strong></TableCell>
                  <TableCell align="right"><strong>{incomeStatement.staffSalaries.staffCount}</strong></TableCell>
                  <TableCell align="right"><strong>{currencySymbol}{formatCurrency(incomeStatement.staffSalaries.totalBase)}</strong></TableCell>
                  <TableCell align="right"><strong>{currencySymbol}{formatCurrency(incomeStatement.staffSalaries.totalBonuses)}</strong></TableCell>
                  <TableCell align="right">
                    <strong>{currencySymbol}{formatCurrency(incomeStatement.staffSalaries.total)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Income Statement Table */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h5" gutterBottom>
            Income Statement
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Period: {new Date(incomeStatement.period.start).toLocaleDateString()} - {new Date(incomeStatement.period.end).toLocaleDateString()}
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>REVENUE</strong></TableCell>
                <TableCell align="right"><strong>Amount ({currencySymbol})</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>School Fees Revenue</TableCell>
                <TableCell align="right">{formatCurrency(incomeStatement.revenue.schoolFees)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Sales Revenue</TableCell>
                <TableCell align="right">{formatCurrency(incomeStatement.revenue.sales)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Canteen Revenue</TableCell>
                <TableCell align="right">{formatCurrency(incomeStatement.revenue.canteen)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Other Income</TableCell>
                <TableCell align="right">{formatCurrency(incomeStatement.revenue.otherIncome)}</TableCell>
              </TableRow>
              <TableRow sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                <TableCell><strong>TOTAL REVENUE</strong></TableCell>
                <TableCell align="right"><strong>{currencySymbol}{formatCurrency(incomeStatement.revenue.total)}</strong></TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2}>
                  <Divider />
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell><strong>EXPENSES</strong></TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
              {incomeStatement.expenses.salaries > 0 && (
                <>
                  <TableRow>
                    <TableCell sx={{ pl: 4 }}><strong>Salaries & Wages</strong></TableCell>
                    <TableCell align="right"><strong>{currencySymbol}{formatCurrency(incomeStatement.expenses.salaries)}</strong></TableCell>
                  </TableRow>
                  {incomeStatement.staffSalaries && incomeStatement.staffSalaries.total > 0 && (
                    <>
                      <TableRow>
                        <TableCell sx={{ pl: 6 }}>Base Salaries</TableCell>
                        <TableCell align="right">{formatCurrency(incomeStatement.staffSalaries.totalBase)}</TableCell>
                      </TableRow>
                      {incomeStatement.staffSalaries.totalBonuses > 0 && (
                        <TableRow>
                          <TableCell sx={{ pl: 6 }}>Bonuses & Allowances</TableCell>
                          <TableCell align="right">{formatCurrency(incomeStatement.staffSalaries.totalBonuses)}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={{ pl: 6, fontStyle: 'italic', fontSize: '0.875rem' }}>
                          ({incomeStatement.staffSalaries.staffCount} staff members)
                        </TableCell>
                        <TableCell align="right"></TableCell>
                      </TableRow>
                    </>
                  )}
                </>
              )}
              {incomeStatement.expenses.utilities > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Utilities</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.utilities)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.maintenance > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Maintenance & Repairs</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.maintenance)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.supplies > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Supplies</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.supplies)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.administrative > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Administrative</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.administrative)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.rent > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Rent & Leasing</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.rent)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.transportation > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Transportation</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.transportation)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.insurance > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Insurance</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.insurance)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.marketing > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Marketing</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.marketing)}</TableCell>
                </TableRow>
              )}
              {incomeStatement.expenses.other > 0 && (
                <TableRow>
                  <TableCell sx={{ pl: 4 }}>Other Expenses</TableCell>
                  <TableCell align="right">{formatCurrency(incomeStatement.expenses.other)}</TableCell>
                </TableRow>
              )}
              <TableRow sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                <TableCell><strong>TOTAL EXPENSES</strong></TableCell>
                <TableCell align="right"><strong>{currencySymbol}{formatCurrency(incomeStatement.expenses.total)}</strong></TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={2}>
                  <Divider />
                </TableCell>
              </TableRow>

              <TableRow sx={{ 
                bgcolor: incomeStatement.netIncome >= 0 ? 'success.light' : 'error.light',
                color: incomeStatement.netIncome >= 0 ? 'success.contrastText' : 'error.contrastText'
              }}>
                <TableCell><strong>NET INCOME</strong></TableCell>
                <TableCell align="right">
                  <strong>{currencySymbol}{formatCurrency(incomeStatement.netIncome)}</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default IncomeStatementTab;
